/**
 * Who the player is on this device: the login token, their profile (look), the room to return
 * to and the device's stable player key. Everything is persisted, so a returning player skips
 * the login screen.
 *
 * The player key is what statistics follow: every login is a new session (a new player id),
 * the key stays with the device — after logging out and in again, after the token expires, after
 * a new version of the game. It is random, made once, sent only at login and never shown.
 */
import type { PlayerId, PlayerProfile, RoomId } from '@cube-rush/shared';
import { batch, computed, type ReadonlySignal, type Signal, signal } from '@preact/signals';
import { type KeyValueStorage, readJson, STORAGE_KEYS, writeJson } from './storage';
import { isRecord, parseProfile } from './validate';

export interface AuthInfo {
  token: string;
  playerId: PlayerId;
  /** ms since the epoch; an expired token is dropped on start. */
  expiresAt: number;
}

export class SessionStore {
  private readonly authSignal: Signal<AuthInfo | null>;
  private readonly profileSignal: Signal<PlayerProfile | null>;
  private readonly lastRoomSignal: Signal<RoomId | null>;

  readonly auth: ReadonlySignal<AuthInfo | null>;
  /** The player's look; kept after logout to prefill the next login. */
  readonly profile: ReadonlySignal<PlayerProfile | null>;
  /** Room to rejoin after a reconnect or reload (sent in `hello`). */
  readonly lastRoomId: ReadonlySignal<RoomId | null>;
  readonly playerId: ReadonlySignal<PlayerId | null>;
  readonly loggedIn: ReadonlySignal<boolean>;

  constructor(
    private readonly storage: KeyValueStorage,
    now: () => number = Date.now,
    private readonly newKey: () => string = randomPlayerKey,
  ) {
    const auth = readJson(storage, STORAGE_KEYS.session, parseAuth);
    // A device logged in before keys existed: the server counts its session's player id as its
    // key, so the statistics of that session go on under the same key after the next login.
    if (auth !== null && readJson(storage, STORAGE_KEYS.playerKey, parsePlayerKey) === null) {
      if (isPlayerKey(auth.playerId)) writeJson(storage, STORAGE_KEYS.playerKey, auth.playerId);
    }
    this.authSignal = signal(auth !== null && auth.expiresAt > now() ? auth : null);
    this.profileSignal = signal(readJson(storage, STORAGE_KEYS.profile, parseProfile));
    this.lastRoomSignal = signal(readJson(storage, STORAGE_KEYS.lastRoom, parseRoomId));
    this.auth = this.authSignal;
    this.profile = this.profileSignal;
    this.lastRoomId = this.lastRoomSignal;
    this.playerId = computed(() => this.authSignal.value?.playerId ?? null);
    this.loggedIn = computed(
      () => this.authSignal.value !== null && this.profileSignal.value !== null,
    );
  }

  signIn(auth: AuthInfo, profile: PlayerProfile): void {
    batch(() => {
      this.authSignal.value = auth;
      this.profileSignal.value = profile;
    });
    writeJson(this.storage, STORAGE_KEYS.session, auth);
    writeJson(this.storage, STORAGE_KEYS.profile, profile);
  }

  /** Forgets the token and the room; the profile stays to prefill the next login. */
  signOut(): void {
    batch(() => {
      this.authSignal.value = null;
      this.lastRoomSignal.value = null;
    });
    this.storage.removeItem(STORAGE_KEYS.session);
    this.storage.removeItem(STORAGE_KEYS.lastRoom);
  }

  setProfile(profile: PlayerProfile): void {
    this.profileSignal.value = profile;
    writeJson(this.storage, STORAGE_KEYS.profile, profile);
  }

  /** The device's stable player key: the stored one, or a new one (kept from now on). */
  playerKey(): string {
    const stored = readJson(this.storage, STORAGE_KEYS.playerKey, parsePlayerKey);
    if (stored !== null) return stored;
    const key = this.newKey();
    writeJson(this.storage, STORAGE_KEYS.playerKey, key);
    return key;
  }

  setLastRoomId(roomId: RoomId): void {
    if (this.lastRoomSignal.peek() === roomId) return;
    this.lastRoomSignal.value = roomId;
    writeJson(this.storage, STORAGE_KEYS.lastRoom, roomId);
  }
}

function parseAuth(raw: unknown): AuthInfo | null {
  if (!isRecord(raw)) return null;
  const { token, playerId, expiresAt } = raw;
  if (typeof token !== 'string' || token === '') return null;
  if (typeof playerId !== 'string' || playerId === '') return null;
  if (typeof expiresAt !== 'number' || !Number.isFinite(expiresAt)) return null;
  return { token, playerId, expiresAt };
}

function parseRoomId(raw: unknown): RoomId | null {
  return typeof raw === 'string' && raw !== '' ? raw : null;
}

/** What the server takes as a player key (LoginRequest.key): 16-64 URL-safe characters. */
function isPlayerKey(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9_-]{16,64}$/.test(value);
}

function parsePlayerKey(raw: unknown): string | null {
  return isPlayerKey(raw) ? raw : null;
}

/** 16 random bytes as base64url (22 characters). */
export function randomPlayerKey(): string {
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
